import { useEffect, useRef } from "react";

/**
 * Themed wrapper around Approve.com's <approve-button> web component.
 * Injects a <style> into its Shadow DOM so the widget adopts TheraTec's
 * cream / gold / ink palette instead of the default blue.
 *
 * Accepts either single-item props (price/model/qty/type) or a bundle via
 * `items` (array of {model, price, qty, type}).
 */

const THEMES = {
  light: {
    // Sits on cream product-page backgrounds
    leftBg: "#FAF9F6",
    leftBorder: "#E5E2DA",
    leftText: "#1C1C1C",
    leftLabel: "#8B8578",
    rightBg: "#1C1C1C",
    rightHover: "#2A2A2A",
    rightText: "#FAF9F6",
    rightAccent: "#C9A961",
    descText: "#8B8578",
  },
  dark: {
    // Sits on dark tile / cart-drawer overlays
    leftBg: "#FAF9F6",
    leftBorder: "#E5E2DA",
    leftText: "#1C1C1C",
    leftLabel: "#8B8578",
    rightBg: "#1C1C1C",
    rightHover: "#2A2A2A",
    rightText: "#FAF9F6",
    rightAccent: "#C9A961",
    descText: "#A8A096",
  },
};

const buildCss = (t) => `
  .button {
    border-radius: 0 !important;
    font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
    letter-spacing: 0.02em;
    transition: transform 400ms ease;
  }
  .button:hover { transform: translateY(-1px); }

  .btn_l_container {
    background: ${t.leftBg} !important;
    color: ${t.leftText} !important;
    border: 1px solid ${t.leftBorder} !important;
    border-right: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 10px 14px !important;
    width: 118px !important;
  }

  .btn_r_container {
    background: ${t.rightBg} !important;
    color: ${t.rightText} !important;
    border: 1px solid ${t.rightBg} !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 10px 16px !important;
    transition: background-color 300ms ease;
  }
  .button:hover > .btn_r_container {
    background: ${t.rightHover} !important;
    border-color: ${t.rightHover} !important;
  }

  .btn_l_container .sm-txt,
  .btn_r_container .sm-txt {
    font-size: 9px !important;
    letter-spacing: 0.18em !important;
    text-transform: uppercase !important;
    color: ${t.leftLabel} !important;
    font-weight: 500 !important;
  }
  .btn_r_container .sm-txt {
    color: ${t.rightAccent} !important;
    opacity: 0.85;
  }

  .teaser {
    font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif !important;
    font-weight: 500 !important;
    font-size: 1.35em !important;
    letter-spacing: 0 !important;
    color: ${t.rightText} !important;
  }

  .desc-text {
    font-family: 'Inter', system-ui, sans-serif !important;
    font-size: 9px !important;
    letter-spacing: 0.22em !important;
    text-transform: uppercase !important;
    color: ${t.descText} !important;
    margin-top: 10px !important;
    font-weight: 500 !important;
  }

  /* Recolor the APPROVE wordmark to charcoal so it reads on cream */
  #svg path[fill="#448ccb"] { fill: ${t.leftText} !important; }
  #svg path[fill="#34444c"] { fill: ${t.leftText} !important; }

  .loader {
    border-top-color: ${t.rightAccent} !important;
  }
`;

const applyTheme = (el, themeKey) => {
  if (!el || !el.shadowRoot) return false;
  const root = el.shadowRoot;
  if (root.querySelector("style[data-theratec-theme]")) return true;
  // Ensure the shadow DOM has actually rendered its internals
  if (!root.querySelector(".btn_l_container")) return false;
  const style = document.createElement("style");
  style.setAttribute("data-theratec-theme", "1");
  style.textContent = buildCss(THEMES[themeKey] || THEMES.light);
  root.appendChild(style);
  return true;
};

export default function ApproveButton({ price, model, qty = 1, type = "Equipment", items, theme = "light", className = "", onClick }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || applyTheme(el, theme)) return;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (applyTheme(el, theme) || tries > 40) clearInterval(iv);
    }, 100);
    return () => clearInterval(iv);
  }, [theme]);

  const props = items
    ? { items: JSON.stringify(items) }
    : { price: String(price), model: String(model || ""), qty: String(qty), type: String(type) };

  return (
    <div onClick={onClick} className={`inline-block cursor-pointer ${className}`} data-testid="approve-button-wrapper" aria-label="Apply for financing with Approve">
      <approve-button ref={ref} application-type="embedded_app" clear-cart-items="true" {...props}></approve-button>
    </div>
  );
}
